<template>
  <div
    class="col-12 col-md-6 pa-0 mb-8"
    :class="[
      question?.PlaceholderText ? 'italic-placeholder' : '',
      question?.StyleClass,
    ]"
  >
    <div>
      <HomeBaseLabel
        :question="question"
        :class="[question?.LabelStyleClass]"
      />
      <div
        v-if="question.Description"
        class="d-flex align-left flex-no-wrap font-14 description"
      >
        {{ question.Description }}
      </div>

      <v-combobox
        :class="[question?.ComboBoxStyleClass]"
        clearable
        dense
        outlined
        :hide-details="
          question.HideDetails != null ? question.HideDetails : 'auto'
        "
        :placeholder="
          question.PlaceholderText
            ? question.PlaceholderText
            : 'Start typing...'
        "
        :items="items"
        item-text="text"
        item-value="text"
        :data-uiid="question.DataUiid"
        :type="question.AnswerControlAttributeType"
        :search-input.sync="search"
        :rules="getRules"
        :loading="propertyDataLoading"
        ref="autocomplete"
        no-filter
        :return-object="false"
        :hide-no-data="hideNoData"
        open-on-clear
        v-model="internalValue"
        append-icon=""
      >
        <template v-if="$refs.autocomplete" v-slot:append>
          <v-icon
            class="icon-check-valid"
            v-if="$refs.autocomplete?.valid"
            color="green"
            size="20"
            >mdi-check-circle</v-icon
          >
          <v-icon
            v-if="!$refs.autocomplete?.valid && $refs.autocomplete?.hasMessages"
            color="red"
            size="20"
            class="icon-check-valid"
            :class="[question?.IconStyleClass]"
            >mdi-alert-circle</v-icon
          >
        </template>
        <template v-slot:selection>
          <span v-text="internalValue"></span>
        </template>
        <template v-slot:item="{ item }">
          <v-list-item @click="addressSelection(item)">
            <v-list-item-content>
              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template v-slot:append-item v-if="hideNoData">
          <v-list-item>
            <v-list-item-content class="hasClick" @click="enterManually()">
              <v-list-item-title
                ><span class="text-decoration-underline"
                  >Can't find your address? Enter manually</span
                ></v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
        </template>
        <template v-slot:no-data>
          <v-list-item class="noHover">
            <v-list-item-content>
              <v-list-item-title>
                <p class="font-14">
                  <strong>Sorry!</strong> We can't find the address right now...
                </p>
                <p class="text-center mt-6">
                  <span
                    class="text-decoration-underline font-16 hasClick"
                    @click="enterManually"
                    >Enter address manually</span
                  >
                </p></v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-combobox>
    </div>
  </div>
</template>

<script>
import AllQuestionsMixin from "@/Mixins/AllQuestionsMixin";
import InterfaceKeyPrefixMixin from "@/Mixins/InterfaceKeyPrefixMixin";
import QuestionValidation from "@/Mixins/Validation/QuestionValidation";
import {
  generateAddress,
  getSelectedAddress,
  loqateEntriesToList,
  resetAddressLayoutAnswers,
  updateAddressLayoutAnswers,
} from "@/common/addresses";
import HomeBaseLabel from "@/components/HomeComponents/HomeBaseComponents/HomeBaseLabel";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import axios from "axios";
import { mapState } from "vuex";

export default {
  name: "InputFindPropertyBase",
  mixins: [QuestionValidation, AllQuestionsMixin, InterfaceKeyPrefixMixin],
  components: {
    HomeBaseLabel,
  },
  props: {
    question: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      search: null,
      entries: [],
      loading: false, // LN data loading (set by mixin)
      isLoading: false, // Address list loading
      getPostcodeResponse: null,
      hideNoData: true,
      addressSearchPromises: [],
      internalValue: this.question.Value,
      type: null,
    };
  },
  watch: {
    search(val) {
      this.isLoading = true;
      this.searchAddressDebounced(val);
    },
    question(newVal) {
      this.internalValue = newVal;
    },
    internalValue(newVal) {
      this.$emit("input", newVal);
    },
  },
  methods: {
    clearSelection() {
      if (
        this.type == "postcode" ||
        this.type == "container" ||
        (this.type == null && !this.internalValue)
      ) {
        const clearButton = this.$refs.autocomplete.$el.querySelector(
          ".v-input__icon--clear .v-icon"
        );
        if (clearButton) {
          clearButton.click();
        }
      }
    },
    handleClickOutside(event) {
      const menuContent = document.querySelector(
        ".v-menu__content.menuable__content__active"
      );
      if (
        !this.$refs.autocomplete.$el.contains(event.target) &&
        !(menuContent && menuContent.contains(event.target))
      ) {
        this.clearSelection();
      }
    },
    resetAllAddressQuestions() {
      resetAddressLayoutAnswers(
        this.allQuestions,
        "LayoutAddressSearchBase",
        this.question.InterfaceKey
      );
    },
    updateAllAddressQuestions(selectedAddress) {
      updateAddressLayoutAnswers(
        this.allQuestions,
        "LayoutAddressSearchBase",
        this.question.InterfaceKey,
        selectedAddress
      );
    },
    setAddressFormVisibility(value) {
      this.$emit("toggleAddressFormVisible", value);
    },
    searchAddressDebounced(val) {
      this.addressSearchPromises.push({ val: val, isValid: true });
      this.addressSearchPromises.forEach((promise) => {
        if (promise.val !== val) promise.isValid = false;
      });

      clearTimeout(this.searchAddressTimeout);
      this.searchAddressTimeout = setTimeout(() => {
        this.searchAddress(val);
      }, 500); /* 500ms throttle */
    },
    searchAddress(val) {
      if (this.search !== null) {
        axios
          .get(
            `https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws?Key=KU12-JD51-XZ77-ZW73&Text=${val}&IsMiddleware=&Container=&Origin=GB&Countries=GB&Limit=&Language=en-gb&Bias=true&Filters=Language:eng`
          )
          .then((response) => {
            const lastAddressSearchPromises =
              this.addressSearchPromises[this.addressSearchPromises.length - 1];
            if (lastAddressSearchPromises.val !== val) {
              return;
            }
            // when the loqate api IS working
            this.resetAllAddressQuestions();
            this.setAddressFormVisibility(false);
            this.getPostcodeResponse = response;

            // when the loqate api IS working AND has INCORRECT parameters
            // returns array containing ONE object (the error itself)
            if (
              // check if array length is 1 AND if array value (object) has a key called Error
              response.data.Items.length === 1 &&
              Object.prototype.hasOwnProperty.call(
                response.data.Items[0],
                "Error"
              )
            ) {
              // when there ARE errors
              console.log(response.data.Items[0]);
              // setting hide-no-data prop to false
              this.hideNoData = false;
            } else {
              // when there are NO errors
              if (response.data.Items.length !== 0) {
                this.entries = response.data.Items;
              } else if (response.data.Items.length === 0) {
                // setting hide-no-data prop to false
                this.hideNoData = false;
                this.entries = [];
              }
            }
          })
          .catch((error) => {
            // when the loqate api is NOT working
            console.log(error);
            this.resetAllAddressQuestions();
            this.setAddressFormVisibility(true);
          })
          .finally(() => (this.isLoading = false));
      }
    },
    addressSelection(item) {
      // the addressSelection method fires ONLY when the user selects an address

      // reset address form
      this.setAddressFormVisibility(false);

      this.type = item.type;

      if (item.type === "postcode" || item.type === "container") {
        // for postcode lookups
        axios
          .get(
            `https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws?Key=KU12-JD51-XZ77-ZW73&Text=&IsMiddleware=&Container=${item.id}&Origin=GB&Countries=GB&Limit=&Language=en-gb&Bias=true&Filters=Language:eng`
          )
          .then((response) => {
            // show the v-autocomplete menu
            this.$refs.autocomplete.isMenuActive = true;
            this.$refs.autocomplete.valid = false;
            this.entries = response.data.Items;
          })
          .catch((error) => {
            console.log(error);

            // show address form
            this.setAddressFormVisibility(true);
          });
      } else {
        // for address lookups
        axios
          .get(
            `https://api.addressy.com/Capture/Interactive/Retrieve/v1.20/json3.ws?Key=KU12-JD51-XZ77-ZW73&Id=${item.id}&Origin=GB&Countries=GB&Limit=&Language=en-gb&Bias=true&Filters=Language:eng`
          )
          .then((response) => {
            const selectedAddress = getSelectedAddress(
              response,
              this.interfaceKeyPrefix
            );

            // remove duplicate address values
            selectedAddress.SolicitorAddressCounty =
              selectedAddress.SolicitorAddressTown ===
              selectedAddress.SolicitorAddressCounty
                ? ""
                : selectedAddress.SolicitorAddressCounty;

            const street = selectedAddress[this.interfaceKeyPrefix + "Street"];
            const town = selectedAddress[this.interfaceKeyPrefix + "Town"];
            const postcode =
              selectedAddress[this.interfaceKeyPrefix + "PostCode"];
            const displayAddress = generateAddress(street, town, postcode);

            this.internalValue = displayAddress;

            // Update all the address question fields
            this.updateAllAddressQuestions(selectedAddress);

            // hide the v-autocomplete menu
            this.$refs.autocomplete.isMenuActive = false;
          })
          .catch((error) => {
            console.log(error);

            // show address form
            this.setAddressFormVisibility(true);
          });
      }
    },
    enterManually() {
      this.resetAllAddressQuestions();
      this.setAddressFormVisibility(true);
      this.$refs.autocomplete.isMenuActive = false;
    },
  },

  computed: {
    ...mapState(QUESTIONS_MODULE, ["form"]),
    propertyDataLoading() {
      return this.loading ? "info" : false;
    },
    items() {
      return loqateEntriesToList(this.entries);
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style lang="scss" scoped>
.hasClick {
  cursor: pointer;
}
.theme--light.v-list-item.noHover:hover {
  background: transparent !important;
  color: rgba(0, 0, 0, 0.87) !important;
}
.bottom-minus-12 {
  margin-bottom: -12px;
}
.description {
  margin-bottom: 13px;
  width: 665px !important;
  color: $black-2 !important;
}
.correspondence-address {
  margin-top: 13px !important;
}
.solicitor-address {
  margin-top: -3px !important;
}
@media #{map-get($display-breakpoints, 'md-and-down')} {
  .description {
    margin-bottom: 13px;
    max-width: 100%;
    color: $black-2 !important;
  }
}
</style>
